import { saveAs } from "file-saver";
import {
  BriefcaseBusinessIcon,
  CalendarDaysIcon,
  DownloadIcon,
  EllipsisIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";

import { CompanyFileViewerDialog } from "./CompanyFileViewerDialog";
import { Column, ColumnHeader, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useDeleteCompanyFile } from "@/hooks/mutations/useDeleteCompanyFile";
import { useGetCompanyFile } from "@/hooks/queries/useGetCompanyFile";
import { useRemoveCompanyFile } from "@/hooks/sets/useRemoveCompanyFile";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { CompanyFile } from "@/types";

const DropdownActions = ({ companyFileId }: { companyFileId: number }) => {
  const { mutate: deleteCompanyFile } = useDeleteCompanyFile();
  const { refetch } = useGetCompanyFile({
    companyFileId,
    enabled: false,
  });
  const removeCompanyFile = useRemoveCompanyFile();

  const handleDeleteCompanyFile = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent the row from being clicked.
    e.stopPropagation();

    mixpanelUtils.trackButtonClick("Delete file", MIXPANEL_SCREENS.COMPANY);
    deleteCompanyFile(
      { companyFileId },
      {
        onSuccess: () => {
          removeCompanyFile({ companyFileId });
        },
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost">
          <EllipsisIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async (e) => {
            e.stopPropagation();
            mixpanelUtils.trackButtonClick(
              "Download file",
              MIXPANEL_SCREENS.COMPANY,
            );
            const { data } = await refetch();
            if (!data) return;
            saveAs(data.signedUrl, data.name);
          }}
        >
          <DownloadIcon className="h-4 w-4" />
          Download
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-500 hover:!text-red-500"
          onClick={handleDeleteCompanyFile}
        >
          <Trash2Icon className="h-4 w-4" />
          Delete file
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const CompanyFilesTable = ({
  className,
  companyFiles,
}: {
  className?: string;
  companyFiles: CompanyFile[];
}) => {
  const {
    selectedResourceId: selectedCompanyFileId,
    setSelectedResourceId: setSelectedCompanyFileId,
  } = useSelectedResourceIdFromUrl({
    paramName: "f",
  });

  const selectedCompanyFile = selectedCompanyFileId
    ? (companyFiles.find((file) => file.id === selectedCompanyFileId) ?? null)
    : null;

  const renderTableHeader = () => {
    return (
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[52%]">
                <ColumnHeader icon={BriefcaseBusinessIcon} label="File Name" />
              </TableHead>
              <TableHead className="w-[20%]">
                <ColumnHeader icon={UsersIcon} label="Created By" />
              </TableHead>
              <TableHead className="w-[20%]">
                <ColumnHeader icon={CalendarDaysIcon} label="Creation Date" />
              </TableHead>
              <TableHead className="w-[8%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
    );
  };

  const renderTableBody = () => {
    return (
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {companyFiles.map((companyFile: CompanyFile) => {
              return (
                <TableRow
                  key={companyFile.id}
                  onClick={() => {
                    setSelectedCompanyFileId(companyFile.id);
                  }}
                >
                  <TableCell className="w-[52%]">
                    <Text>{companyFile.name}</Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {companyFile.user.firstName} {companyFile.user.lastName}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {new Intl.DateTimeFormat("en-US").format(
                        new Date(companyFile.createdAt),
                      )}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[8%]">
                    <DropdownActions companyFileId={companyFile.id} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderDialogs = () => {
    return (
      <CompanyFileViewerDialog
        companyFileId={selectedCompanyFile?.id}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedCompanyFileId(null);
          }
        }}
        open={!!selectedCompanyFile}
      />
    );
  };

  return (
    <Column className={cn("overflow-auto", className)}>
      {renderTableHeader()}
      {renderTableBody()}
      {renderDialogs()}
    </Column>
  );
};
